import { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { Timestamp } from 'firebase/firestore';

// Social Media types (matching municipality app structure)
export enum SocialMediaPlatform {
  FACEBOOK = 'facebook',
  TWITTER = 'twitter',
  INSTAGRAM = 'instagram',
  LINKEDIN = 'linkedin',
  YOUTUBE = 'youtube',
  TIKTOK = 'tiktok',
  PINTEREST = 'pinterest',
  SNAPCHAT = 'snapchat',
  REDDIT = 'reddit',
  DISCORD = 'discord',
  THREADS = 'threads',
}

export interface SocialMediaLink {
  id: string;
  platform: SocialMediaPlatform;
  name: string;
  url: string;
  municipalityId: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  authorName: string;
}

interface SocialMediaListResponse {
  items: SocialMediaLink[];
  total: number;
}

// Helper to convert Firestore Timestamp to Date
const toDate = (value: unknown): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return new Date();
};

// Convert Firestore data to SocialMediaLink
const convertFirestoreData = (data: unknown, id: string, municipalityId: string): SocialMediaLink => {
  const docData = data as Record<string, unknown>;

  return {
    id,
    platform: (docData.platform as SocialMediaPlatform) || SocialMediaPlatform.FACEBOOK,
    name: (docData.name as string) || '',
    url: (docData.url as string) || '',
    municipalityId,
    createdAt: toDate(docData.createdAt),
    updatedAt: toDate(docData.updatedAt),
    createdBy: (docData.createdBy as string) || '',
    authorName: (docData.authorName as string) || '',
  };
};

export const useMunicipalitySocialMedia = (municipalityId: string | null) => {
  const [data, setData] = useState<SocialMediaListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setLoading(false);
      setData(null);
      return;
    }

    const fetchSocialMedia = async () => {
      try {
        setLoading(true);
        setError(null);

        const socialMediaCollection = collection(db, 'municipalities', municipalityId, 'socialMedia');
        const snapshot = await getDocs(query(socialMediaCollection, orderBy('platform', 'asc')));

        const items: SocialMediaLink[] = [];
        snapshot.forEach((doc) => {
          const link = convertFirestoreData(doc.data(), doc.id, municipalityId);
          items.push(link);
        });

        setData({
          items,
          total: items.length,
        });
      } catch (err) {
        console.error('Error fetching municipality social media:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch municipality social media');
      } finally {
        setLoading(false);
      }
    };

    fetchSocialMedia();
  }, [municipalityId]);

  return { data, loading, error };
};
